import {
  Plus,
  Pencil,
  Trash2,
  ArrowRightLeft,
  FileText,
  CreditCard,
  CircleDot,
  Clock,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { AuditLog } from '@/types'

interface AuditTimelineProps {
  logs: AuditLog[]
}

function timeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

function getActionIcon(action: string) {
  switch (action) {
    case 'create':
      return Plus
    case 'update':
      return Pencil
    case 'delete':
      return Trash2
    case 'status_change':
      return ArrowRightLeft
    default:
      return CircleDot
  }
}

function getActionColor(action: string) {
  switch (action) {
    case 'create':
      return 'bg-positive/15 text-positive border-positive/20'
    case 'update':
      return 'bg-blue-500/15 text-blue-400 border-blue-500/20'
    case 'delete':
      return 'bg-destructive/15 text-destructive border-destructive/20'
    case 'status_change':
      return 'bg-warning/15 text-warning border-warning/20'
    default:
      return 'bg-muted text-muted-foreground border-border'
  }
}

function getTypeIcon(type: string) {
  if (type === 'PurchaseOrder') return FileText
  if (type === 'Payment') return CreditCard
  return CircleDot
}

function humanizeField(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatFieldValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '--'
  if (field === 'amount' && typeof value === 'string') {
    const num = parseFloat(value)
    if (!isNaN(num)) return formatCurrency(num)
  }
  if (field === 'status' && typeof value === 'string') {
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }
  return String(value)
}

export default function AuditTimeline({ logs }: AuditTimelineProps) {
  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Clock className="size-8 mb-3 opacity-40" />
        <p className="text-sm">No changes recorded yet</p>
      </div>
    )
  }

  return (
    <div className="relative space-y-0">
      {/* Vertical connecting line */}
      <div className="absolute left-[17px] top-3 bottom-3 w-px bg-border" />

      {logs.map((log, index) => {
        const ActionIcon = getActionIcon(log.action)
        const TypeIcon = getTypeIcon(log.auditable_type)
        const actionColor = getActionColor(log.action)
        const changedFields = log.changed_fields || {}
        const fieldEntries = Object.entries(changedFields)

        return (
          <div key={log.id} className="relative flex gap-4 pb-6 last:pb-0">
            {/* Timeline dot */}
            <div
              className={cn(
                'relative z-10 flex size-[35px] shrink-0 items-center justify-center rounded-full border',
                actionColor
              )}
            >
              <ActionIcon className="size-3.5" />
            </div>

            {/* Content */}
            <div className="flex-1 pt-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <TypeIcon className="size-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    {log.auditable_type.replace(/([A-Z])/g, ' $1').trim()} #{log.auditable_id}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">{log.action}</span>
                {log.user?.email && (
                  <span className="text-xs text-muted-foreground/60">
                    by {log.user.email}
                  </span>
                )}
                <span className="text-xs text-muted-foreground/60">
                  {timeAgo(log.created_at)}
                </span>
              </div>

              {fieldEntries.length > 0 && (
                <div className="mt-2 space-y-1">
                  {fieldEntries.map(([field, changes]) => {
                    const changeArr = Array.isArray(changes) ? changes : [null, changes]
                    const [oldVal, newVal] = changeArr

                    return (
                      <div
                        key={field}
                        className="flex items-center gap-2 text-xs rounded-md bg-muted/30 px-2.5 py-1.5"
                      >
                        <span className="font-medium text-muted-foreground">
                          {humanizeField(field)}:
                        </span>
                        {oldVal !== null && oldVal !== undefined ? (
                          <>
                            <span className="text-destructive/80 line-through">
                              {formatFieldValue(field, oldVal)}
                            </span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-positive">
                              {formatFieldValue(field, newVal)}
                            </span>
                          </>
                        ) : (
                          <span className="text-foreground">
                            {formatFieldValue(field, newVal)}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

